import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { chatbotAPI, paymentAPI, handleAPIError, getStatusColor } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { 
  Bot, 
  Upload, 
  MessageCircle, 
  Plus, 
  Minus, 
  Save,
  Sparkles,
  Globe,
  Building,
  HelpCircle,
  User,
  DollarSign,
  CreditCard,
  Shield,
  MessageSquare,
  Check,
  CheckCircle,
  Loader2,
  Clock
} from 'lucide-react';

const ChatbotCreator = ({ onClose, onSuccess, existingChatbots = [] }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdChatbot, setCreatedChatbot] = useState(null);

  // Check if user is customer (needs payment)
  const isCustomer = () => user?.role === 'customer';
  
  // Check if user has reached the one chatbot limit
  const hasReachedLimit = () => {
    return isCustomer() && existingChatbots && existingChatbots.length >= 1;
  };
  
  // Total steps: 6 for chatbot creation (payment happens before creation for customers)
  const totalSteps = 6;

  const [formData, setFormData] = useState({
    company_name: '',
    company_website: '',
    company_logo: null,  // Changed to ChatbotFile structure
    customization: {
      // New 6 color system
      chat_window_background: '#1f2937',
      user_message_background: '#8b5cf6',
      live_message_background: '#a855f7', 
      chatbot_message_background: '#374151',
      thinking_dots_color: '#c084fc',
      send_button_color: '#7c3aed',
      personality: 'professional',
      custom_personality: '', // Add custom personality field
      greeting_message: 'Hello! How can I help you today?'
    },
    advanced_settings: {
      enabled: false,
      placeholder_text: 'Type your message...',
      welcome_message: 'Hi! I\'m here to help you. What can I assist you with today?',
      suggestions: ['What services do you offer?', 'How can I contact support?', 'Tell me about your pricing'],
      llm_model: 'gpt-4o-mini',
      rate_limiting: {
        enabled: false,
        message_limit: 5,
        time_period: 5,
        limit_message: 'You have reached the message limit. Please try again later.'
      },
      domain_whitelist: {
        enabled: false,
        domains: []
      }
    },
    faqs: [{ question: '', answer: '' }],
    additional_info: '',
    additional_files: [],
    contact_info: {
      contact_method: 'builtin_chat',
      whatsapp_number: ''
    }
  });
  
  const [logoPreview, setLogoPreview] = useState(null);
  const [phoneError, setPhoneError] = useState('');

  // Phone number validation function
  const validatePhoneNumber = (phone) => {
    if (!phone) return true; // Optional field
    
    // Basic validation - must start with + and have at least 8 digits
    const phoneRegex = /^\+[1-9]\d{7,14}$/;
    return phoneRegex.test(phone);
  };

  const personalities = [
    { value: 'professional', label: 'Professional', description: 'Formal and business-focused' },
    { value: 'friendly', label: 'Friendly', description: 'Warm and approachable' },
    { value: 'casual', label: 'Casual', description: 'Relaxed and conversational' },
    { value: 'helpful', label: 'Helpful', description: 'Solution-oriented and supportive' },
    { value: 'custom', label: 'Custom', description: 'Define your own unique personality' },
  ];

  const llmModels = [
    { value: 'gpt-4o', label: 'GPT-4o', credits: 5, description: 'Most advanced OpenAI model' },
    { value: 'gpt-4o-mini', label: 'GPT-4o mini', credits: 1, description: 'Efficient and fast' },
    { value: 'o4-mini', label: 'o4-mini', credits: 1, description: 'Optimized for speed' },
    { value: 'o3', label: 'o3', credits: 5, description: 'High-performance reasoning' },
    { value: 'gpt-4.1', label: 'GPT-4.1', credits: 5, description: 'Enhanced capabilities' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 mini', credits: 1, description: 'Lightweight version' },
    { value: 'gpt-4.1-nano', label: 'GPT-4.1 nano', credits: 1, description: 'Ultra-efficient' },
    { value: 'claude-4-sonnet', label: 'Claude 4 Sonnet', credits: 5, description: 'Anthropic\'s flagship model' },
    { value: 'claude-3.5-haiku', label: 'Claude 3.5 Haiku', credits: 1, description: 'Fast and efficient' },
    { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash', credits: 1, description: 'Google\'s speed-optimized' },
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', credits: 1, description: 'Enhanced flash model' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', credits: 5, description: 'Google\'s premium model' }
  ];

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const parts = field.split('.');
      if (parts.length === 2) {
        const [parent, child] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        }));
      } else if (parts.length === 3) {
        const [parent, child, grandchild] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: {
              ...prev[parent][child],
              [grandchild]: value
            }
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // File size validation (5MB limit for logo)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select a logo image under 5MB",
          variant: "destructive",
        });
        return;
      }

      // File type validation (only images for logo)
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid file type",
          description: "Please select an image file (PNG, JPG, SVG, etc.)",
          variant: "destructive",
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        const logoFile = {
          id: Math.random().toString(36).substr(2, 9),
          filename: file.name,
          content_type: file.type,
          file_data: base64,
          file_size: file.size
        };
        
        setFormData(prev => ({
          ...prev,
          company_logo: logoFile
        }));
        setLogoPreview(event.target.result);
        
        toast({
          title: "Logo uploaded successfully",
          description: `${file.name} has been uploaded successfully`,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({
      ...prev,
      company_logo: null
    }));
    setLogoPreview(null);
    
    toast({
      title: "Logo removed",
      description: "Logo has been removed successfully",
    });
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      // Check individual file size (max 500MB per file)
      if (file.size > 500 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: `${file.name} is too large. Please select files under 500MB each`,
          variant: "destructive",
        });
        return;
      }
      
      // Check total size won't exceed 2GB
      const currentTotalSize = formData.additional_files.reduce((total, f) => total + f.file_size, 0);
      if (currentTotalSize + file.size > 2 * 1024 * 1024 * 1024) {
        toast({
          title: "Total size limit exceeded",
          description: "Adding this file would exceed the 2GB total limit. Please remove some files first.",
          variant: "destructive",
        });
        return;
      }

      // Check file type
      const allowedTypes = [
        'image/', 'application/pdf', 'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel', 
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/', 'application/rtf'
      ];
      
      const isAllowed = allowedTypes.some(type => file.type.startsWith(type));
      if (!isAllowed) {
        toast({
          title: "Unsupported file type",
          description: `${file.name} is not a supported file type`,
          variant: "destructive",
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        const newFile = {
          id: Math.random().toString(36).substr(2, 9),
          filename: file.name,
          content_type: file.type,
          file_data: base64,
          file_size: file.size
        };
        
        setFormData(prev => ({
          ...prev,
          additional_files: [...prev.additional_files, newFile]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (fileId) => {
    setFormData(prev => ({
      ...prev,
      additional_files: prev.additional_files.filter(f => f.id !== fileId)
    }));
  };

  const addFAQ = () => {
    if (formData.faqs.length < 10) {
      setFormData(prev => ({
        ...prev,
        faqs: [...prev.faqs, { question: '', answer: '' }]
      }));
    }
  };

  const removeFAQ = (index) => {
    if (formData.faqs.length > 1) {
      setFormData(prev => ({
        ...prev,
        faqs: prev.faqs.filter((_, i) => i !== index)
      }));
    }
  };

  const updateFAQ = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      faqs: prev.faqs.map((faq, i) => 
        i === index ? { ...faq, [field]: value } : faq
      )
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.company_name.trim() && formData.company_website.trim();
      case 2:
        return true; // Logo is optional
      case 3:
        // Validate custom personality if selected
        if (formData.customization.personality === 'custom' && !formData.customization.custom_personality.trim()) {
          return false;
        }
        return formData.customization.greeting_message.trim();
      case 4:
        return formData.faqs.every(faq => faq.question.trim() && faq.answer.trim());
      case 5:
        return true; // Advanced settings are optional
      case 6:
        // Validate WhatsApp number if selected
        if (formData.contact_info.contact_method === 'whatsapp') {
          const isValidPhone = validatePhoneNumber(formData.contact_info.whatsapp_number);
          if (!isValidPhone) {
            setPhoneError('Please enter a valid international phone number');
            return false;
          }
          if (!formData.contact_info.whatsapp_number.trim()) {
            setPhoneError('WhatsApp number is required when WhatsApp is selected');
            return false;
          }
        }
        setPhoneError('');
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    } else {
      toast({
        title: "Please complete all required fields",
        description: "Fill in all required information before proceeding",
        variant: "destructive",
      });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(6)) {
      return;
    }

    // Check total file size limit (2GB = 2,147,483,648 bytes)
    const totalFileSize = formData.additional_files.reduce((total, file) => total + file.file_size, 0);
    const maxTotalSize = 2 * 1024 * 1024 * 1024; // 2GB in bytes
    
    if (totalFileSize > maxTotalSize) {
      toast({
        title: "Total file size too large",
        description: `Total file size is ${(totalFileSize / (1024 * 1024 * 1024)).toFixed(2)}GB. Please keep total files under 2GB.`,
        variant: "destructive",
      });
      return;
    }

    // For customers, require deposit payment BEFORE creating chatbot
    if (isCustomer()) {
      try {
        setIsSubmitting(true);
        
        // Send the complete form data to be stored in database before payment
        const response = await paymentAPI.createChatbotCreationSession({
          ...formData,
          // Ensure all nested objects are included
          customization: { ...formData.customization },
          advanced_settings: { ...formData.advanced_settings },
          faqs: [...formData.faqs],
          additional_files: [...formData.additional_files],
          contact_info: { ...formData.contact_info }
        });
        
        if (response.data.checkout_url) {
          // No need to store in sessionStorage anymore - data is stored in database
          // Redirect to Stripe checkout for deposit payment
          window.location.href = response.data.checkout_url;
        } else {
          throw new Error('No checkout URL received');
        }
      } catch (error) {
        console.error('Error creating payment session:', error);
        const errorMessage = handleAPIError(error, 'Failed to initiate payment for chatbot creation');
        
        toast({
          title: "Payment failed",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // For non-customers (demo, admin, employee), create chatbot directly
      try {
        setIsSubmitting(true);

        // Prepare form data for submission
        const submitData = {
          ...formData,
          additional_info: formData.additional_info || ''
        };

        // Create the chatbot
        const response = await chatbotAPI.createChatbot(submitData);
        console.log('Chatbot created:', response.data);
        
        const newChatbot = response.data;
        setCreatedChatbot(newChatbot);

        toast({
          title: "Chatbot created successfully!",
          description: "Your chatbot has been created and is ready for review.",
        });

        // Call success and close
        if (onSuccess) {
          onSuccess(newChatbot);
        }

      } catch (error) {
        console.error('Error creating chatbot:', error);
        const errorMessage = handleAPIError(error, 'Failed to create chatbot');
        toast({
          title: "Error creating chatbot",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };



  const stepTitles = [
    "Company Information",
    "Logo & Branding", 
    "Chatbot Personality",
    "FAQ & Knowledge Base",
    "Advanced Settings",
    "Review & Submit",
    ...(isCustomer() ? ["Payment & Activation"] : [])
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      {hasReachedLimit() ? (
        // One chatbot limit reached message
        <Card className="w-full max-w-lg bg-gray-900 border-violet-500/20">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-xl text-white">One Chatbot Limit</CardTitle>
            <CardDescription className="text-gray-400 mt-2">
              You've reached your chatbot limit for this account.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <div className="p-4 bg-gradient-to-r from-yellow-900/30 to-orange-900/30 rounded-lg border border-yellow-500/30">
              <p className="text-yellow-300 text-sm mb-2">
                <MessageSquare className="w-4 h-4 inline mr-2" />
                Current Plan: One chatbot per account
              </p>
              <p className="text-gray-400 text-sm">
                You can create a new chatbot after your current one is completed and archived.
              </p>
            </div>
            
            <div className="space-y-3">
              <p className="text-gray-300 text-sm">
                Your current chatbot: <span className="font-semibold text-white">{existingChatbots[0]?.company_name}</span>
              </p>
              <p className="text-gray-400 text-xs">
                Status: <Badge className={`${getStatusColor ? getStatusColor(existingChatbots[0]?.status) : 'bg-gray-600'} text-xs`}>
                  {existingChatbots[0]?.status?.replace('_', ' ') || 'Unknown'}
                </Badge>
              </p>
            </div>
            
            <div className="flex space-x-3 pt-4">
              <Button
                onClick={onClose}
                className="flex-1 bg-violet-600 hover:bg-violet-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Got it
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        // Normal chatbot creation flow
        <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 border-violet-500/20">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl text-white flex items-center">
                <Bot className="w-8 h-8 mr-3 text-violet-400" />
                Create Your Chatbot
              </CardTitle>
              <CardDescription className="text-gray-400 mt-2">
                Step {currentStep} of {totalSteps}: {stepTitles[currentStep - 1]}
              </CardDescription>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white">
              ×
            </Button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between mb-2">
              {stepTitles.map((title, index) => (
                <span 
                  key={index}
                  className={`text-xs ${index + 1 <= currentStep ? 'text-violet-400' : 'text-gray-500'}`}
                >
                  {index + 1}
                </span>
              ))}
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-violet-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Step 1: Company Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Building className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Company Information</h3>
                <p className="text-gray-400">Tell us about your company so we can customize your chatbot</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-300">Company Name *</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    placeholder="Enter your company name"
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Company Website *</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <Input
                      value={formData.company_website}
                      onChange={(e) => handleInputChange('company_website', e.target.value)}
                      placeholder="https://your-website.com"
                      className="bg-gray-800 border-gray-600 text-white pl-10"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Additional Information</Label>
                <Textarea
                  value={formData.additional_info}
                  onChange={(e) => handleInputChange('additional_info', e.target.value)}
                  placeholder="Tell us more about your business, target audience, or specific requirements..."
                  rows={4}
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>

              {/* File Upload Section */}
              <div className="space-y-3">
                <Label className="text-gray-300">Additional Files (Optional)</Label>
                <p className="text-gray-400 text-sm">
                  Upload documents, images, or files that will help us understand your business better.
                  <br />Supported: Images, PDFs, Word docs, Excel files, Text files (max 500MB per file, 2GB total)
                </p>
                {formData.additional_files.length > 0 && (
                  <div className="text-sm">
                    <span className="text-gray-400">
                      Total size: {(formData.additional_files.reduce((total, file) => total + file.file_size, 0) / (1024 * 1024)).toFixed(1)}MB / 2048MB
                    </span>
                  </div>
                )}
                
                {/* File Upload Area */}
                <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 hover:border-violet-500 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                  />
                  <div className="text-center">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <Button
                      onClick={() => document.getElementById('file-upload').click()}
                      variant="outline"
                      className="border-gray-600 text-gray-300"
                    >
                      Choose Files
                    </Button>
                  </div>
                </div>

                {/* Uploaded Files List */}
                {formData.additional_files.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-gray-300">Uploaded Files:</Label>
                    {formData.additional_files.map((file) => (
                      <div key={file.id} className="flex items-center justify-between p-2 bg-gray-700 rounded">
                        <div className="flex items-center space-x-2">
                          <Upload className="w-4 h-4 text-violet-400" />
                          <span className="text-white text-sm">{file.filename}</span>
                          <span className="text-gray-400 text-xs">
                            ({(file.file_size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <Button
                          onClick={() => removeFile(file.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 p-1"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Logo & Branding */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Upload className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Logo & Branding</h3>
                <p className="text-gray-400">Upload your company logo to personalize your chatbot</p>
              </div>

              <div className="space-y-4">
                {/* Logo Upload Area */}
                <div>
                  <Label className="text-gray-300">Company Logo (Optional)</Label>
                  <p className="text-gray-400 text-sm mb-3">
                    Upload your company logo for chatbot branding.
                    <br />Supported: PNG, JPG, SVG (max 5MB)
                  </p>
                  
                  <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 hover:border-violet-500 transition-colors">
                    {logoPreview ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-center">
                          <img 
                            src={logoPreview} 
                            alt="Logo preview" 
                            className="max-w-32 max-h-32 rounded-lg border border-gray-600"
                          />
                        </div>
                        <div className="text-center">
                          <p className="text-green-400 mb-2">✓ Logo uploaded successfully!</p>
                          <div className="flex justify-center space-x-3">
                            <Button
                              onClick={() => document.getElementById('logo-upload').click()}
                              variant="outline"
                              size="sm"
                              className="border-gray-600 text-gray-300"
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              Replace
                            </Button>
                            <Button
                              onClick={removeLogo}
                              variant="outline"
                              size="sm"
                              className="border-red-600 text-red-400 hover:bg-red-600/10"
                            >
                              <Minus className="w-4 h-4 mr-1" />
                              Remove
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center space-y-4">
                        <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                        <div>
                          <p className="text-white mb-2">Upload your company logo</p>
                          <p className="text-gray-400 text-sm">Drag & drop or click to browse</p>
                        </div>
                        <Button
                          onClick={() => document.getElementById('logo-upload').click()}
                          className="bg-violet-600 hover:bg-violet-700"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Choose Logo File
                        </Button>
                      </div>
                    )}
                    
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                  </div>
                </div>

                {/* Logo Guidelines */}
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-600">
                  <h4 className="text-white font-medium mb-2">Logo Guidelines</h4>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Use high-quality images for best results</li>
                    <li>• Square or rectangular logos work best</li>
                    <li>• Transparent backgrounds (PNG) recommended</li>
                    <li>• Logo will be automatically resized for chatbot display</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Chatbot Personality */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Chatbot Personality</h3>
                <p className="text-gray-400">Customize how your chatbot interacts with customers</p>
              </div>

              <div className="space-y-6">
                <div className="space-y-3">
                  <Label className="text-gray-300">Personality Type</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {personalities.slice(0, 4).map((personality) => (
                      <div
                        key={personality.value}
                        className={`p-4 rounded-lg border cursor-pointer transition-all ${
                          formData.customization.personality === personality.value
                            ? 'border-violet-500 bg-violet-500/10'
                            : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                        }`}
                        onClick={() => handleInputChange('customization.personality', personality.value)}
                      >
                        <h4 className="text-white font-medium">{personality.label}</h4>
                        <p className="text-gray-400 text-sm">{personality.description}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Custom Personality Option - Larger Button */}
                  <div className="mt-4">
                    <div
                      className={`p-6 rounded-lg border cursor-pointer transition-all ${
                        formData.customization.personality === 'custom'
                          ? 'border-violet-500 bg-violet-500/10'
                          : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                      }`}
                      onClick={() => handleInputChange('customization.personality', 'custom')}
                    >
                      <div className="text-center">
                        <h4 className="text-white font-bold text-lg mb-2">Custom</h4>
                        <p className="text-gray-400 text-sm">Define your own unique personality</p>
                      </div>
                    </div>
                    
                    {/* Custom Personality Description Textarea */}
                    {formData.customization.personality === 'custom' && (
                      <div className="mt-4 space-y-2">
                        <Label className="text-gray-300">Custom Personality Description *</Label>
                        <Textarea
                          value={formData.customization.custom_personality}
                          onChange={(e) => handleInputChange('customization.custom_personality', e.target.value)}
                          placeholder="Describe how your chatbot should interact with customers. Be specific about tone, style, and approach..."
                          rows={4}
                          className="bg-gray-800 border-gray-600 text-white"
                        />
                        <p className="text-gray-400 text-xs">
                          Example: "Be enthusiastic and energetic, use emojis occasionally, speak like a knowledgeable friend who's excited to help, and always maintain a positive attitude."
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Greeting Message *</Label>
                  <Textarea
                    value={formData.customization.greeting_message}
                    onChange={(e) => handleInputChange('customization.greeting_message', e.target.value)}
                    placeholder="Enter the first message your chatbot will send to visitors"
                    rows={3}
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Chat Window Background</Label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.customization.chat_window_background}
                        onChange={(e) => handleInputChange('customization.chat_window_background', e.target.value)}
                        className="w-12 h-10 rounded border-gray-600"
                      />
                      <Input
                        value={formData.customization.chat_window_background}
                        onChange={(e) => handleInputChange('customization.chat_window_background', e.target.value)}
                        className="bg-gray-800 border-gray-600 text-white text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">User Message Background</Label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.customization.user_message_background}
                        onChange={(e) => handleInputChange('customization.user_message_background', e.target.value)}
                        className="w-12 h-10 rounded border-gray-600"
                      />
                      <Input
                        value={formData.customization.user_message_background}
                        onChange={(e) => handleInputChange('customization.user_message_background', e.target.value)}
                        className="bg-gray-800 border-gray-600 text-white text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Live Message Background</Label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.customization.live_message_background}
                        onChange={(e) => handleInputChange('customization.live_message_background', e.target.value)}
                        className="w-12 h-10 rounded border-gray-600"
                      />
                      <Input
                        value={formData.customization.live_message_background}
                        onChange={(e) => handleInputChange('customization.live_message_background', e.target.value)}
                        className="bg-gray-800 border-gray-600 text-white text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Chatbot Message Background</Label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.customization.chatbot_message_background}
                        onChange={(e) => handleInputChange('customization.chatbot_message_background', e.target.value)}
                        className="w-12 h-10 rounded border-gray-600"
                      />
                      <Input
                        value={formData.customization.chatbot_message_background}
                        onChange={(e) => handleInputChange('customization.chatbot_message_background', e.target.value)}
                        className="bg-gray-800 border-gray-600 text-white text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Thinking Dots Color</Label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.customization.thinking_dots_color}
                        onChange={(e) => handleInputChange('customization.thinking_dots_color', e.target.value)}
                        className="w-12 h-10 rounded border-gray-600"
                      />
                      <Input
                        value={formData.customization.thinking_dots_color}
                        onChange={(e) => handleInputChange('customization.thinking_dots_color', e.target.value)}
                        className="bg-gray-800 border-gray-600 text-white text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Send Button Color</Label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.customization.send_button_color}
                        onChange={(e) => handleInputChange('customization.send_button_color', e.target.value)}
                        className="w-12 h-10 rounded border-gray-600"
                      />
                      <Input
                        value={formData.customization.send_button_color}
                        onChange={(e) => handleInputChange('customization.send_button_color', e.target.value)}
                        className="bg-gray-800 border-gray-600 text-white text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: FAQ & Knowledge Base */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <HelpCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">FAQ & Knowledge Base</h3>
                <p className="text-gray-400">Add common questions and answers your chatbot should know</p>
              </div>

              <div className="space-y-4">
                {formData.faqs.map((faq, index) => (
                  <Card key={index} className="bg-gray-800 border-gray-600">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="outline" className="border-violet-500 text-violet-400">
                          FAQ {index + 1}
                        </Badge>
                        {formData.faqs.length > 1 && (
                          <Button
                            onClick={() => removeFAQ(index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 p-1"
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-gray-300">Question *</Label>
                          <Input
                            value={faq.question}
                            onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                            placeholder="e.g., What are your business hours?"
                            className="bg-gray-700 border-gray-600 text-white"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-gray-300">Answer *</Label>
                          <Textarea
                            value={faq.answer}
                            onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                            placeholder="e.g., We're open Monday to Friday, 9 AM to 6 PM EST."
                            rows={3}
                            className="bg-gray-700 border-gray-600 text-white"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {formData.faqs.length < 10 && (
                  <Button
                    onClick={addFAQ}
                    variant="outline"
                    className="w-full border-dashed border-violet-500 text-violet-400 hover:bg-violet-500/10"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Another FAQ
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Advanced Settings */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Advanced Settings</h3>
                <p className="text-gray-400">Configure advanced options for your chatbot (optional)</p>
              </div>

              <div className="space-y-6">
                {/* Advanced Settings Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-gray-600">
                  <div>
                    <h4 className="text-white font-medium">Enable Advanced Settings</h4>
                    <p className="text-gray-400 text-sm">Configure additional options like rate limiting, domain restrictions, and custom messages</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.advanced_settings.enabled}
                      onChange={(e) => handleInputChange('advanced_settings.enabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                  </label>
                </div>

                {/* Advanced Settings Content */}
                {formData.advanced_settings.enabled && (
                  <div className="space-y-6">
                    {/* Placeholder Text */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">Placeholder Text</Label>
                      <Input
                        value={formData.advanced_settings.placeholder_text}
                        onChange={(e) => handleInputChange('advanced_settings.placeholder_text', e.target.value)}
                        placeholder="Type your message..."
                        className="bg-gray-800 border-gray-600 text-white"
                      />
                    </div>

                    {/* Welcome Message */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">Welcome Message</Label>
                      <Textarea
                        value={formData.advanced_settings.welcome_message}
                        onChange={(e) => handleInputChange('advanced_settings.welcome_message', e.target.value)}
                        placeholder="Hi! I'm here to help you. What can I assist you with today?"
                        rows={3}
                        className="bg-gray-800 border-gray-600 text-white"
                      />
                    </div>

                    {/* LLM Model Selection */}
                    <div className="space-y-3">
                      <Label className="text-gray-300">AI Model</Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {llmModels.map((model) => (
                          <div
                            key={model.value}
                            className={`p-3 rounded-lg border cursor-pointer transition-all ${
                              formData.advanced_settings.llm_model === model.value
                                ? 'border-violet-500 bg-violet-500/10'
                                : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                            }`}
                            onClick={() => handleInputChange('advanced_settings.llm_model', model.value)}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h5 className="text-white font-medium">{model.label}</h5>
                                <p className="text-gray-400 text-xs">{model.description}</p>
                              </div>
                              <Badge variant="outline" className="border-violet-500 text-violet-400 text-xs">
                                {model.credits} credits
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Rate Limiting */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-gray-300">Rate Limiting</Label>
                          <p className="text-gray-400 text-sm">Limit the number of messages per user</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.advanced_settings.rate_limiting.enabled}
                            onChange={(e) => handleInputChange('advanced_settings.rate_limiting.enabled', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                        </label>
                      </div>

                      {formData.advanced_settings.rate_limiting.enabled && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-gray-300">Message Limit</Label>
                              <Input
                                type="number"
                                value={formData.advanced_settings.rate_limiting.message_limit}
                                onChange={(e) => handleInputChange('advanced_settings.rate_limiting.message_limit', parseInt(e.target.value) || 5)}
                                min="1"
                                max="100"
                                className="bg-gray-800 border-gray-600 text-white"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-gray-300">Time Period (minutes)</Label>
                              <Input
                                type="number"
                                value={formData.advanced_settings.rate_limiting.time_period}
                                onChange={(e) => handleInputChange('advanced_settings.rate_limiting.time_period', parseInt(e.target.value) || 5)}
                                min="1"
                                max="60"
                                className="bg-gray-800 border-gray-600 text-white"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-300">Show this message when the message limit is hit</Label>
                            <Input
                              value={formData.advanced_settings.rate_limiting.limit_message}
                              onChange={(e) => handleInputChange('advanced_settings.rate_limiting.limit_message', e.target.value)}
                              placeholder="Too many messages in a row"
                              className="bg-gray-800 border-gray-600 text-white"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 6: Review & Submit */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Review & Submit</h3>
                <p className="text-gray-400">Review your chatbot configuration before submitting</p>
              </div>

              {/* Deposit Warning for Customers */}
              {isCustomer() && (
                <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Clock className="w-4 h-4 text-yellow-400" />
                    </div>
                    <div>
                      <h4 className="text-yellow-300 font-semibold mb-2">⚠️ Important Notice</h4>
                      <div className="space-y-2 text-yellow-200 text-sm">
                        <p className="font-medium">
                          This configuration is not final! Our team will work with you to perfect your chatbot.
                        </p>
                        <p>
                          The upcoming payment is only a <strong>$200 deposit</strong> to secure your project and begin development. You can make changes throughout the process.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <Card className="bg-gray-800 border-gray-600">
                  <CardContent className="p-4">
                    <h4 className="text-white font-semibold mb-3">Company Information</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-400">Company:</span>
                        <p className="text-white">{formData.company_name}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Website:</span>
                        <p className="text-white">{formData.company_website}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gray-800 border-gray-600">
                  <CardContent className="p-4">
                    <h4 className="text-white font-semibold mb-3">Chatbot Configuration</h4>
                    <div className="space-y-4 text-sm">
                      <div>
                        <span className="text-gray-400">Personality:</span>
                        <span className="text-white ml-2 capitalize">{formData.customization.personality}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Greeting:</span>
                        <p className="text-white">{formData.customization.greeting_message}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">FAQs:</span>
                        <span className="text-white ml-2">{formData.faqs.length} questions added</span>
                      </div>
                      
                      {/* Color Configuration */}
                      <div>
                        <span className="text-gray-400">Color Configuration:</span>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-4 h-4 rounded border border-gray-500" 
                              style={{backgroundColor: formData.customization.chat_window_background}}
                            ></div>
                            <span className="text-white text-xs">Chat Window</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-4 h-4 rounded border border-gray-500" 
                              style={{backgroundColor: formData.customization.user_message_background}}
                            ></div>
                            <span className="text-white text-xs">User Messages</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-4 h-4 rounded border border-gray-500" 
                              style={{backgroundColor: formData.customization.live_message_background}}
                            ></div>
                            <span className="text-white text-xs">Live Messages</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-4 h-4 rounded border border-gray-500" 
                              style={{backgroundColor: formData.customization.chatbot_message_background}}
                            ></div>
                            <span className="text-white text-xs">Bot Messages</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-4 h-4 rounded border border-gray-500" 
                              style={{backgroundColor: formData.customization.thinking_dots_color}}
                            ></div>
                            <span className="text-white text-xs">Thinking Dots</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-4 h-4 rounded border border-gray-500" 
                              style={{backgroundColor: formData.customization.send_button_color}}
                            ></div>
                            <span className="text-white text-xs">Send Button</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Advanced Settings */}
                      {formData.advanced_settings.enabled && (
                        <div>
                          <span className="text-gray-400">Advanced Settings:</span>
                          <div className="ml-4 mt-2 space-y-1">
                            <p className="text-white text-xs">Model: {llmModels.find(m => m.value === formData.advanced_settings.llm_model)?.label}</p>
                            <p className="text-white text-xs">Welcome: {formData.advanced_settings.welcome_message.substring(0, 50)}...</p>
                            {formData.advanced_settings.rate_limiting.enabled && (
                              <p className="text-white text-xs">Rate Limiting: {formData.advanced_settings.rate_limiting.message_limit} msgs/{formData.advanced_settings.rate_limiting.time_period}min</p>
                            )}
                            {formData.advanced_settings.domain_whitelist.enabled && (
                              <p className="text-white text-xs">Domain Whitelist: {formData.advanced_settings.domain_whitelist.domains.length} domains</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <div className="bg-violet-900/20 border border-violet-500/30 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-3">How would you like us to contact you?</h4>
                  <div className="space-y-3">
                    <div 
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.contact_info.contact_method === 'whatsapp'
                          ? 'border-violet-500 bg-violet-500/10'
                          : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                      }`}
                      onClick={() => handleInputChange('contact_info.contact_method', 'whatsapp')}
                    >
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          checked={formData.contact_info.contact_method === 'whatsapp'}
                          onChange={() => handleInputChange('contact_info.contact_method', 'whatsapp')}
                          className="text-violet-500"
                        />
                        <div>
                          <h5 className="text-white font-medium">WhatsApp Call</h5>
                          <p className="text-gray-400 text-sm">We'll contact you via WhatsApp call</p>
                        </div>
                      </div>
                      {formData.contact_info.contact_method === 'whatsapp' && (
                        <div className="mt-3">
                          <Label className="text-gray-300">WhatsApp Number</Label>
                          <div className="mt-1">
                            <PhoneInput
                              defaultCountry="sk"
                              value={formData.contact_info.whatsapp_number}
                              onChange={(phone) => {
                                handleInputChange('contact_info.whatsapp_number', phone);
                                setPhoneError(''); // Clear error when user types
                              }}
                              inputProps={{
                                className: "bg-gray-700 border-gray-600 text-white rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent",
                                placeholder: "Enter your WhatsApp number"
                              }}
                              countrySelectorStyleProps={{
                                className: "bg-gray-700 border-gray-600 text-white rounded-l-md"
                              }}
                              dialCodePreviewStyleProps={{
                                className: "text-white"
                              }}
                            />
                            {phoneError && (
                              <p className="text-xs text-red-400 mt-1">
                                {phoneError}
                              </p>
                            )}
                            {formData.contact_info.whatsapp_number && !phoneError && (
                              <p className="text-xs text-gray-400 mt-1">
                                Please ensure this is a valid WhatsApp number with the correct country code
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div 
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.contact_info.contact_method === 'microsoft_teams'
                          ? 'border-violet-500 bg-violet-500/10'
                          : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                      }`}
                      onClick={() => handleInputChange('contact_info.contact_method', 'microsoft_teams')}
                    >
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          checked={formData.contact_info.contact_method === 'microsoft_teams'}
                          onChange={() => handleInputChange('contact_info.contact_method', 'microsoft_teams')}
                          className="text-violet-500"
                        />
                        <div>
                          <h5 className="text-white font-medium">Microsoft Teams</h5>
                          <p className="text-gray-400 text-sm">Wait for instructions in built-in chat and emails</p>
                        </div>
                      </div>
                    </div>

                    <div 
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.contact_info.contact_method === 'builtin_chat'
                          ? 'border-violet-500 bg-violet-500/10'
                          : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                      }`}
                      onClick={() => handleInputChange('contact_info.contact_method', 'builtin_chat')}
                    >
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          checked={formData.contact_info.contact_method === 'builtin_chat'}
                          onChange={() => handleInputChange('contact_info.contact_method', 'builtin_chat')}
                          className="text-violet-500"
                        />
                        <div>
                          <h5 className="text-white font-medium">Built-in Chat Only</h5>
                          <p className="text-gray-400 text-sm">We will contact you soon via our chat system</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-violet-900/20 border border-violet-500/30 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-2">What happens next?</h4>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Our team will review your requirements</li>
                    <li>• We'll build and customize your chatbot</li>
                    <li>• Testing and quality assurance</li>
                    <li>• Deployment to your website</li>
                    <li>• Training and documentation provided</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Step 7: Payment & Activation (Customers only) */}
          {currentStep === 7 && isCustomer() && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Step 7: Project Deposit
                </h3>
                <p className="text-gray-400 text-lg">
                  Pay your $200 deposit to secure your project and begin custom development
                </p>
              </div>

              {/* Important Configuration Warning */}
              <div className="bg-amber-900/30 border border-amber-500/50 rounded-lg p-6 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-amber-300 font-bold text-lg mb-3">⚠️ Important - This is NOT Your Final Configuration!</h4>
                    <div className="space-y-3 text-amber-200">
                      <p className="font-semibold text-base">
                        The information you've entered is just a starting point. Our team will work closely with you to perfect your chatbot!
                      </p>
                      <div className="bg-amber-800/20 border border-amber-600/30 rounded p-4">
                        <h5 className="font-semibold text-amber-300 mb-2">🏗️ What This Payment Is:</h5>
                        <ul className="text-sm space-y-1 ml-4">
                          <li>• <strong>A deposit only</strong> - not the full payment</li>
                          <li>• Secures your project and starts the development process</li>
                          <li>• Allows our team to begin working on your customized solution</li>
                        </ul>
                      </div>
                      <div className="bg-blue-900/20 border border-blue-600/30 rounded p-4">
                        <h5 className="font-semibold text-blue-300 mb-2">🤝 What Happens Next:</h5>
                        <ul className="text-sm space-y-1 ml-4">
                          <li>• Our team contacts you to discuss your exact needs</li>
                          <li>• We refine and customize everything to perfection</li>
                          <li>• You can request changes throughout development</li>
                          <li>• Final payment only after you're completely satisfied</li>
                        </ul>
                      </div>
                      <p className="text-sm font-medium text-amber-300 mt-3 bg-amber-900/30 p-3 rounded">
                        💡 <strong>Rest assured:</strong> Nothing is final yet! This deposit simply gets the ball rolling so we can create exactly what you need.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chatbot Summary */}
              {createdChatbot && (
                <div className="bg-gray-800/50 rounded-lg p-4 mb-6">
                  <h4 className="text-white font-semibold mb-3">Your Chatbot Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Company:</span>
                      <span className="text-gray-300">{createdChatbot.company_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Website:</span>
                      <span className="text-gray-300">{createdChatbot.company_website}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Status:</span>
                      <span className="text-yellow-400">Pending Payment</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Details */}
              <div className="space-y-4">
                <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-6">
                  <div className="flex items-center justify-between text-white mb-4">
                    <div>
                      <h4 className="text-xl font-bold">Project Deposit</h4>
                      <p className="text-gray-400 text-sm">To secure your project and start development</p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-violet-400">$200.00</div>
                      <div className="text-xs text-gray-500">Deposit Only</div>
                    </div>
                  </div>
                  
                  <div className="bg-violet-900/20 border border-violet-500/30 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Shield className="w-5 h-5 text-violet-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="text-violet-300 font-medium mb-1">Secure Payment Processing</h4>
                        <p className="text-violet-200 text-sm">
                          Processed securely through Stripe. We never store your payment information. This is a deposit to begin your custom chatbot development.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* What happens next */}
                <div className="space-y-3">
                  <h4 className="text-white font-medium">What happens after payment:</h4>
                  <div className="space-y-3">
                    {[
                      { icon: Check, text: 'Your chatbot is immediately activated' },
                      { icon: MessageSquare, text: 'Our team will discuss your specific requirements' },
                      { icon: Bot, text: 'Customization and refinements as needed' },
                      { icon: Sparkles, text: 'Final testing and deployment' }
                    ].map((step, index) => (
                      <div key={index} className="flex items-center space-x-3 text-sm">
                        <div className="w-6 h-6 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-violet-400 text-xs font-bold">{index + 1}</span>
                        </div>
                        <step.icon className="w-4 h-4 text-violet-400 flex-shrink-0" />
                        <span className="text-gray-300">{step.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-700">
            <Button
              onClick={prevStep}
              variant="outline"
              disabled={currentStep === 1}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Previous
            </Button>

            <div className="flex space-x-3">
              {currentStep < 6 ? (
                <Button
                  onClick={nextStep}
                  className="bg-violet-600 hover:bg-violet-700 px-8"
                >
                  Next
                </Button>
              ) : currentStep === 6 ? (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 px-8"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {isCustomer() ? 'Create & Continue to Payment' : 'Create Chatbot'}
                    </>
                  )}
                </Button>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default ChatbotCreator;